/**
 * EditAgentDialog - Modal para editar função e status de um agente
 * 
 * Permite alterar:
 * - Função: Agente / Supervisor / Administrador
 * - Perfil de Permissão (para supervisores)
 * - Status: Ativo <-> Inativo
 * - Vincular a outros tenants
 * 
 * Regras:
 * - Proprietário original não pode ser editado
 * - Não pode inativar a si mesmo
 * - Supervisores precisam de perfil de permissão
 */

import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Loader2, AlertCircle, Link2, Inbox, Info, Eye } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { FeatureHelp } from '@/components/ui/feature-help';
import LinkAgentToTenantDialog from './LinkAgentToTenantDialog';
import { Avatar3DSelector } from '@/components/ui/avatar-3d-selector';
import { is3DAvatarId } from '@/lib/utils/avatar-3d';
import { getStorageUrl } from '@/lib/storage';

// Interface para inbox vinculada ao agente
interface LinkedInbox {
  id: string;
  name: string;
  channel_type: string;
  identifier: string | null;
}

// Interface para inbox do tenant (usado na seção de visibilidade total)
interface TenantInbox {
  id: string;
  name: string;
  channel_type: string;
  identifier: string | null;
}

// Tipo de função disponível
type AgentRole = 'agent' | 'supervisor' | 'owner';

// Interface do agente para edição
interface Agent {
  id: string;
  user_id: string;
  is_owner: boolean;
  is_active: boolean;
  joined_at: string;
  email?: string;
  full_name?: string;
  avatar_url?: string | null;
}

// Interface do perfil de permissão
interface PermissionProfile {
  id: string;
  name: string;
}

interface EditAgentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agent: Agent | null;
  currentUserId: string;
  tenantId: string;
  onSuccess: () => void;
}

const EditAgentDialog = ({
  open,
  onOpenChange,
  agent,
  currentUserId,
  tenantId,
  onSuccess,
}: EditAgentDialogProps) => {
  // Estados do formulário
  const [role, setRole] = useState<AgentRole>('agent');
  const [isActive, setIsActive] = useState(true);
  const [name, setName] = useState('');
  const [permissionProfileId, setPermissionProfileId] = useState<string | null>(null);
  const [observerModeEnabled, setObserverModeEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Estados para seleção de avatar
  const [selectedAvatarId, setSelectedAvatarId] = useState<string | null>(null);
  const [customAvatarFile, setCustomAvatarFile] = useState<File | null>(null);
  const [customPreviewUrl, setCustomPreviewUrl] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState(false);
  // Estado para avatar de storage existente (não é avatar 3D, ex: users/.../avatar.jpg)
  const [existingStorageAvatar, setExistingStorageAvatar] = useState<string | null>(null);

  // Estados dos perfis de permissão
  const [profiles, setProfiles] = useState<PermissionProfile[]>([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);

  // Estados para vincular a outro tenant
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [hasMultipleTenants, setHasMultipleTenants] = useState(false);
  
  // Estados para inboxes vinculadas (acesso restrito)
  const [linkedInboxes, setLinkedInboxes] = useState<LinkedInbox[]>([]);
  const [loadingLinkedInboxes, setLoadingLinkedInboxes] = useState(false);
  const [agentTenantCount, setAgentTenantCount] = useState(1);

  // =========================================================================
  // v1.0.5: Estados para Visibilidade Total de Conversas
  // =========================================================================
  const [allTenantInboxes, setAllTenantInboxes] = useState<TenantInbox[]>([]);
  const [viewAllInboxIds, setViewAllInboxIds] = useState<Set<string>>(new Set());
  const [loadingViewAll, setLoadingViewAll] = useState(false);

  // Carrega perfis + contexto consolidado do agente em uma única RPC server-side.
  // Substitui 4 SELECTs diretos (tenant_members, user_roles, inbox_agents, inboxes)
  // por get_agent_edit_context, evitando expor nomes de tabelas no Network.
  useEffect(() => {
    if (open && tenantId) {
      fetchProfiles();
      fetchAgentTenantCount();
      fetchAgentEditContext();
    }
  }, [open, tenantId, agent?.user_id]);

  // Sincroniza estados quando o agente muda
  useEffect(() => {
    if (agent) {
      setIsActive(agent.is_active);
      setName(agent.full_name || '');
      
      // Verifica se o avatar_url atual é um ID de avatar 3D
      if (agent.avatar_url && is3DAvatarId(agent.avatar_url)) {
        setSelectedAvatarId(agent.avatar_url);
        setCustomPreviewUrl(null);
        setExistingStorageAvatar(null);
      } else if (agent.avatar_url) {
        // É uma imagem customizada (path de storage)
        // CORREÇÃO: Guarda referência ao avatar de storage existente
        setSelectedAvatarId(null);
        setCustomPreviewUrl(null);
        setExistingStorageAvatar(agent.avatar_url);
      } else {
        setSelectedAvatarId(null);
        setCustomPreviewUrl(null);
        setExistingStorageAvatar(null);
      }
    }
  }, [agent]);
  
  // Cleanup do preview URL quando componente desmonta ou arquivo muda
  useEffect(() => {
    return () => {
      if (customPreviewUrl && customPreviewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(customPreviewUrl);
      }
    };
  }, [customPreviewUrl]);
  
  // Limpa erro de avatar quando usuário seleciona algo
  useEffect(() => {
    if (selectedAvatarId || customAvatarFile || customPreviewUrl || existingStorageAvatar) {
      setAvatarError(false);
    }
  }, [selectedAvatarId, customAvatarFile, customPreviewUrl, existingStorageAvatar]);
  
  /**
   * Handler para upload de imagem customizada
   */
  const handleCustomUpload = (file: File) => {
    // Limpa preview anterior
    if (customPreviewUrl && customPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(customPreviewUrl);
    }
    
    // Cria novo preview
    const previewUrl = URL.createObjectURL(file);
    setCustomAvatarFile(file);
    setCustomPreviewUrl(previewUrl);
    setSelectedAvatarId(null); // Limpa seleção de avatar 3D
    setExistingStorageAvatar(null); // Limpa referência ao avatar de storage existente
  };
  
  /**
   * Handler para limpar imagem customizada
   */
  const handleClearCustom = () => {
    if (customPreviewUrl && customPreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(customPreviewUrl);
    }
    setCustomAvatarFile(null);
    setCustomPreviewUrl(null);
    setExistingStorageAvatar(null); // Também limpa avatar de storage existente
  };

  /**
   * Carrega TODO o contexto da tela em uma única chamada server-side.
   * Substitui: fetchAdminTenantsCount, fetchCurrentRole, fetchLinkedInboxes e fetchViewAllInboxes.
   * A RPC get_agent_edit_context valida acesso de admin e retorna apenas dados permitidos.
   */
  const fetchAgentEditContext = async () => {
    if (!agent?.user_id) return;

    setLoadingLinkedInboxes(true);
    setLoadingViewAll(true);
    try {
      const { data, error } = await supabase.rpc('get_agent_edit_context', {
        p_agent_user_id: agent.user_id,
        p_tenant_hint: tenantId,
      });

      if (error) throw error;
      const ctx = (data ?? {}) as {
        current_role?: string | null;
        permission_profile_id?: string | null;
        observer_mode_enabled?: boolean;
        admin_has_other_tenants?: boolean;
        tenant_inboxes?: TenantInbox[];
        linked_inboxes?: LinkedInbox[];
        view_all_inbox_ids?: string[];
      };

      // 1) Admin gerencia outros tenants? (controla botão "Vincular a outro tenant")
      setHasMultipleTenants(Boolean(ctx.admin_has_other_tenants));

      // 2) Role atual do agente (mesma regra de derivação anterior)
      if (ctx.current_role === 'tenant_admin' && !agent.is_owner) {
        setRole('owner');
      } else if (agent.is_owner) {
        setRole('owner');
      } else if (ctx.current_role === 'tenant_supervisor') {
        setRole('supervisor');
        setPermissionProfileId(ctx.permission_profile_id ?? null);
      } else if (ctx.current_role) {
        setRole('agent');
        setPermissionProfileId(ctx.permission_profile_id ?? null);
      } else {
        setRole(agent.is_owner ? 'owner' : 'agent');
      }

      // 3) Observer mode
      setObserverModeEnabled(Boolean(ctx.observer_mode_enabled));

      // 4) Inboxes vinculadas (acesso restrito) e visibilidade total
      setLinkedInboxes(ctx.linked_inboxes ?? []);
      setAllTenantInboxes(ctx.tenant_inboxes ?? []);
      setViewAllInboxIds(new Set(ctx.view_all_inbox_ids ?? []));
    } catch (error) {
      logger.error('[EditAgentDialog] Erro ao carregar contexto do agente:', String(error));
      setRole(agent?.is_owner ? 'owner' : 'agent');
      setLinkedInboxes([]);
      setAllTenantInboxes([]);
      setViewAllInboxIds(new Set());
    } finally {
      setLoadingLinkedInboxes(false);
      setLoadingViewAll(false);
    }
  };

  /**
   * Busca quantos tenants o agente está vinculado
   */
  const fetchAgentTenantCount = async () => {
    if (!agent?.user_id) return;

    try {
      // Server-side: substitui SELECT direto em tenant_members por RPC
      const { data, error } = await supabase.rpc('count_user_active_tenants', {
        p_user_id: agent.user_id,
        p_tenant_hint: tenantId,
      });

      if (error) throw error;
      setAgentTenantCount(Number(data) || 1);
    } catch (error) {
      logger.error('[EditAgentDialog] Erro ao contar tenants do agente:', String(error));
    }
  };

  /**
   * v1.0.5: Alterna view_all_conversations para uma inbox específica
   */
  const toggleViewAllForInbox = (inboxId: string) => {
    setViewAllInboxIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(inboxId)) {
        newSet.delete(inboxId);
      } else {
        newSet.add(inboxId);
      }
      return newSet;
    });
  };

  const fetchProfiles = async () => {
    setLoadingProfiles(true);
    try {
      // Server-side: substitui SELECT direto em permission_profiles por RPC
      const { data, error } = await supabase.rpc('get_permission_profiles_list', {
        p_tenant_hint: tenantId,
      });

      if (error) throw error;
      const activeOnly = (data || [])
        .filter((p: any) => p.is_active)
        .map((p: any) => ({ id: p.id, name: p.name }));
      setProfiles(activeOnly);
    } catch (error) {
      console.error('Erro ao buscar perfis:', error);
    } finally {
      setLoadingProfiles(false);
    }
  };

  // fetchCurrentRole removido: a role e o observer_mode agora vêm de get_agent_edit_context.


  // Verifica se está editando a si mesmo
  const isCurrentUser = agent?.user_id === currentUserId;

  /**
   * Salva as alterações do agente
   */
  const handleSave = async () => {
    if (!agent) return;

    // Validação: avatar é obrigatório
    // Considera: avatar 3D selecionado, arquivo customizado, preview de upload OU avatar de storage existente
    const hasAvatar = selectedAvatarId || customAvatarFile || customPreviewUrl || existingStorageAvatar;
    if (!hasAvatar) {
      setAvatarError(true);
      toast.error('Selecione um avatar ou envie uma foto própria');
      return;
    }

    // Validação: não pode inativar a si mesmo
    if (isCurrentUser && !isActive) {
      toast.error('Você não pode inativar sua própria conta');
      return;
    }

    // Validação: supervisor precisa de perfil
    if (role === 'supervisor' && !permissionProfileId) {
      toast.error('Selecione um perfil de permissão para o supervisor');
      return;
    }

    // Validação preventiva: verificar limite ao reativar usuário inativo (UX)
    // A proteção real está no trigger validate_member_reactivation_limit no banco
    if (!agent.is_active && isActive) {
      const { data: limits } = await supabase.rpc("get_tenant_effective_limits", { p_tenant_id: tenantId });
      const maxAgents = (limits as any)?.effective_max_agents || 3;

      // Limite ilimitado (-1): permitir sem restrição
      if (maxAgents !== -1) {
        // Server-side: substitui SELECT direto em tenant_members por RPC
        const { data: countData } = await supabase.rpc('count_tenant_active_members', {
          p_tenant_hint: tenantId,
        });
        const count = Number(countData) || 0;

        if ((count || 0) >= maxAgents) {
          toast.error(`Limite de agentes atingido (${count}/${maxAgents}). Contrate add-ons ou faça upgrade do plano.`);
          return;
        }
      }
    }

    setSaving(true);
    try {
      // Determina o novo valor do avatar_url
      let newAvatarUrl: string | null = agent.avatar_url || null;
      
      // Se tem arquivo customizado, faz upload
      if (customAvatarFile) {
        const fileExt = customAvatarFile.name.split('.').pop() || 'jpg';
        const filePath = `users/${agent.user_id}/avatar.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('avatars')
          .upload(filePath, customAvatarFile, { upsert: true });
        
        if (uploadError) {
          logger.error('[EditAgentDialog] Erro ao fazer upload do avatar:', String(uploadError));
          toast.error('Erro ao fazer upload da imagem');
          setSaving(false);
          return;
        }
        
        newAvatarUrl = filePath;
      } else if (selectedAvatarId) {
        // Se selecionou um avatar 3D, salva o ID
        newAvatarUrl = selectedAvatarId;
      }
      
      // Atualiza nome e avatar na tabela profiles
      const trimmedName = name.trim();
      const profileUpdates: { full_name?: string | null; avatar_url?: string | null } = {};
      
      if (trimmedName !== (agent.full_name || '')) {
        profileUpdates.full_name = trimmedName || null;
      }
      
      // Só atualiza avatar_url se mudou
      if (newAvatarUrl !== agent.avatar_url) {
        profileUpdates.avatar_url = newAvatarUrl;
      }
      
      if (Object.keys(profileUpdates).length > 0) {
        const { error: profileError } = await supabase
          .from('profiles')
          .update(profileUpdates)
          .eq('id', agent.user_id);

        if (profileError) throw profileError;
      }

      // Mapeia role do select para o formato do banco
      const dbRole = role === 'owner'
        ? 'tenant_admin'
        : role === 'supervisor'
          ? 'tenant_supervisor'
          : 'agent';

      // Server-side: substitui UPDATEs/INSERTs diretos em tenant_members,
      // user_roles e inbox_agents por uma única RPC atômica e auditada.
      // Cobre: is_active, observer_mode, role + permission_profile_id (com upsert)
      // e sincronização de view_all_conversations para todas as inboxes ativas.
      const { error: saveError } = await supabase.rpc('save_agent_edit_context', {
        p_agent_user_id: agent.user_id,
        p_member_id: agent.id,
        p_role: dbRole,
        p_permission_profile_id:
          role === 'supervisor' || role === 'agent' ? permissionProfileId : null,
        p_is_active: isActive,
        p_observer_mode_enabled:
          role === 'supervisor' || role === 'owner' ? observerModeEnabled : false,
        p_view_all_inbox_ids: Array.from(viewAllInboxIds),
        p_tenant_hint: tenantId,
      });

      if (saveError) throw saveError;


      toast.success('Agente atualizado com sucesso');
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      console.error('Erro ao atualizar agente:', error);
      // Exibe a mensagem real vinda da RPC (ex.: regras de auto-edição) para facilitar diagnóstico
      const message = (error as { message?: string })?.message;
      toast.error(message || 'Erro ao atualizar agente');

    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Editar Agente</DialogTitle>
          <DialogDescription>
            Altere a função e status do agente {agent?.full_name || agent?.email}.
          </DialogDescription>
        </DialogHeader>

        {/* Container com scroll interno para o conteúdo */}
        <div className="flex-1 overflow-y-auto space-y-6 py-4 pr-2">
          {/* Campo Nome (editável) */}
          <div className="space-y-2">
            <Label htmlFor="name">Nome</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nome do agente"
            />
          </div>

          {/* Seletor de Avatar 3D */}
          <Avatar3DSelector
            value={selectedAvatarId}
            onChange={(avatarId) => {
              setSelectedAvatarId(avatarId);
              // Se selecionar avatar 3D, limpa avatar de storage existente
              if (avatarId) {
                setExistingStorageAvatar(null);
              }
            }}
            onCustomUpload={handleCustomUpload}
            customPreviewUrl={customPreviewUrl || (existingStorageAvatar ? getStorageUrl(existingStorageAvatar) : null)}
            onClearCustom={handleClearCustom}
            disabled={saving}
            label="Avatar"
            required={true}
            error={avatarError}
          />

          {/* Campo Email (somente visualização) */}
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              value={agent?.email || ''}
              disabled
              className="bg-muted cursor-not-allowed"
            />
            <p className="text-xs text-muted-foreground">
              O email não pode ser alterado.
            </p>
          </div>

          {/* Seletor de Função - Bloqueado para auto-edição */}
          <div className="space-y-2">
            <Label htmlFor="role">Função</Label>
            <Select 
              value={role} 
              onValueChange={(value: AgentRole) => {
                setRole(value);
                // Limpa perfil se não for supervisor
                if (value !== 'supervisor') {
                  setPermissionProfileId(null);
                }
              }}
              disabled={isCurrentUser}
            >
              <SelectTrigger className={isCurrentUser ? 'opacity-60 cursor-not-allowed' : ''}>
                <SelectValue placeholder="Selecione a função" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="agent">Agente</SelectItem>
                <SelectItem value="supervisor">Supervisor</SelectItem>
                <SelectItem value="owner">Administrador</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {isCurrentUser && 'Você não pode alterar sua própria função.'}
              {!isCurrentUser && role === 'owner' && 'Administradores têm acesso total ao tenant.'}
              {!isCurrentUser && role === 'supervisor' && 'Supervisores têm permissões configuráveis.'}
              {!isCurrentUser && role === 'agent' && 'Agentes têm acesso ao chat. Com perfil de permissão, podem acessar outros menus.'}
            </p>
          </div>

          {/* Seletor de Perfil de Permissão (para supervisores e agentes) */}
          {/* v1.1.0: Agentes também podem ter perfil de permissão (opcional) */}
          {(role === 'supervisor' || role === 'agent') && (
            <div className="space-y-2">
              <Label htmlFor="profile">
                Perfil de Permissão {role === 'supervisor' ? '*' : '(opcional)'}
              </Label>
              {loadingProfiles ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Carregando perfis...
                </div>
              ) : profiles.length === 0 ? (
                <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/20 rounded-md">
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                  <p className="text-sm text-amber-600 dark:text-amber-400">
                    Nenhum perfil de permissão cadastrado. Crie um perfil em Configurações primeiro.
                  </p>
                </div>
              ) : (
                <Select 
                  value={permissionProfileId || 'none'} 
                  onValueChange={(value) => setPermissionProfileId(value === 'none' ? null : value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={role === 'agent' ? 'Nenhum (apenas Chat)' : 'Selecione o perfil'} />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Opção para limpar seleção (apenas agentes) */}
                    {role === 'agent' && (
                      <SelectItem value="none">Nenhum (apenas Chat)</SelectItem>
                    )}
                    {profiles.map((profile) => (
                      <SelectItem key={profile.id} value={profile.id}>
                        {profile.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <p className="text-xs text-muted-foreground">
                {role === 'supervisor' 
                  ? 'Define quais menus e ações o supervisor terá acesso.' 
                  : 'Se não definido, o agente terá acesso apenas ao Chat.'}
              </p>
            </div>
          )}

          {/* Toggle de Status */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="is-active">Status Ativo</Label>
              <p className="text-xs text-muted-foreground">
                Agentes inativos não podem acessar o sistema.
              </p>
            </div>
            <Switch
              id="is-active"
              checked={isActive}
              onCheckedChange={setIsActive}
              disabled={isCurrentUser}
            />
          </div>

          {/* Aviso se está editando a si mesmo */}
          {isCurrentUser && (
            <p className="text-sm text-amber-600 dark:text-amber-400">
              Você não pode inativar sua própria conta.
            </p>
          )}

          {/* Toggle Modo Observador - Apenas para Supervisores/Admins */}
          {(role === 'supervisor' || role === 'owner') && (
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <Label htmlFor="observer-mode">Modo Observador</Label>
                  <FeatureHelp featureKey="observerMode" size="sm" />
                </div>
                <p className="text-xs text-muted-foreground">
                  Permite visualizar conversas sem marcar como lidas
                </p>
              </div>
              <Switch
                id="observer-mode"
                checked={observerModeEnabled}
                onCheckedChange={setObserverModeEnabled}
              />
            </div>
          )}

          {/* Seção: Inboxes Vinculadas (somente leitura) */}
          <Separator />
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Inbox className="h-4 w-4 text-muted-foreground" />
              <Label>Inboxes com Acesso Restrito</Label>
            </div>
            
            {loadingLinkedInboxes ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Carregando inboxes...
              </div>
            ) : linkedInboxes.length === 0 ? (
              <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-md">
                <Info className="h-4 w-4 text-muted-foreground mt-0.5" />
                <div className="text-sm text-muted-foreground">
                  <p>Este agente não está vinculado a nenhuma inbox com acesso restrito.</p>
                  <p className="text-xs mt-1">
                    Inboxes sem restrição são acessíveis por todos os agentes.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  {linkedInboxes.map((inbox) => (
                    <Badge 
                      key={inbox.id} 
                      variant="secondary"
                      className="flex items-center gap-1.5"
                    >
                      <span>{inbox.name}</span>
                      <span className="text-xs text-muted-foreground">
                        ({inbox.channel_type})
                      </span>
                    </Badge>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground flex items-center gap-1">
                  <Info className="h-3 w-3" />
                  Para alterar, edite as configurações de cada inbox.
                </p>
              </div>
            )}
          </div>

          {/* ================================================================= */}
          {/* v1.0.5: Seção Visibilidade Total de Conversas                     */}
          {/* ================================================================= */}
          <Separator />
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Eye className="h-4 w-4 text-muted-foreground" />
              <Label>Visibilidade Total de Conversas</Label>
            </div>
            
            <p className="text-xs text-muted-foreground">
              Selecione as inboxes onde este agente poderá visualizar TODAS as conversas, sem filtro de atribuição.
            </p>

            {loadingViewAll ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Carregando inboxes...
              </div>
            ) : allTenantInboxes.length === 0 ? (
              <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-md">
                <Info className="h-4 w-4 text-muted-foreground mt-0.5" />
                <p className="text-sm text-muted-foreground">
                  Nenhuma inbox ativa encontrada neste tenant.
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-3">
                {allTenantInboxes.map((inbox) => (
                  <label
                    key={inbox.id}
                    className="flex items-center gap-3 py-1.5 px-2 rounded-md hover:bg-muted/50 cursor-pointer"
                  >
                    <Checkbox
                      checked={viewAllInboxIds.has(inbox.id)}
                      onCheckedChange={() => toggleViewAllForInbox(inbox.id)}
                    />
                    <span className="text-sm flex-1">{inbox.name}</span>
                    <span className="text-xs text-muted-foreground">
                      ({inbox.channel_type})
                    </span>
                  </label>
                ))}
              </div>
            )}

            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Info className="h-3 w-3" />
              Inboxes marcadas permitem ao agente ver todas as conversas, não apenas as atribuídas a ele.
            </p>
          </div>

          {/* Seção: Vincular a Outro Tenant */}
          {hasMultipleTenants && (
            <>
              <Separator />
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Tenants Vinculados</Label>
                    <p className="text-xs text-muted-foreground">
                      Este agente está vinculado a {agentTenantCount} tenant(s).
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setLinkDialogOpen(true)}
                  >
                    <Link2 className="h-4 w-4 mr-2" />
                    Vincular a Outro
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={saving || (role === 'supervisor' && profiles.length === 0)}
          >
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Modal para vincular a outro tenant */}
      <LinkAgentToTenantDialog
        open={linkDialogOpen}
        onOpenChange={setLinkDialogOpen}
        agent={agent}
        currentTenantId={tenantId}
        currentUserId={currentUserId}
        onSuccess={() => {
          fetchAgentTenantCount();
          onSuccess();
        }}
      />
    </Dialog>
  );
};

export default EditAgentDialog;
